import { BuildStatus } from '@cadstrom/cadstrom-shared';
import { Utils } from '@cadstrom/sigma-engine-shared';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useDeferredValue, useMemo, useRef } from 'react';
import * as z from 'zod';

import { ActiveDesignContextProvider } from '#components/cad-renderer/context/active-design-context-provider.js';
import { CadstromColorProviderContextProvider } from '#components/cad-renderer/context/cadstrom-color-provider-context-provider.js';
import { CoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { RegisterDesignSearchProvider } from '#components/command-bar/providers/design-search-provider/design-search-provider.js';
import { RegisterModuleSearchProvider } from '#components/command-bar/providers/module-search-provider/module-search-provider.js';
import { RegisterNetSearchProvider } from '#components/command-bar/providers/net-search-provider/net-search-provider.js';
import { Loading } from '#components/loading.js';
import { ResizableLayout } from '#components/resizable-layout/resizable-layout.js';
import { useValidateResolutionEditorSingalMapping } from '#state/stores/validation-store/validate-resolution-editor.js';
import { getUnifiedModuleResolution } from './get-unified-module-resolution.js';
import { ModuleResolutionTable } from './module-resolution-table/module-resolution-table.js';
import { ProjectRender } from './project-render.js';
import { RegisterComponentInferenceProvider } from './register-component-inference-provider.js';
import { ResolutionDetails } from './resolution-details/resolution-details.js';
import { createUnresolvedModuleResolution } from './use-update-module-resolutions.js';

import type { RefCallback } from 'react';
import type { CadRendererRef, ZoomToModuleOptions } from '#components/cad-renderer/cad-renderer.js';

// Warns experts that edits made while the BOM is not yet final can be clobbered by the customer's
// BOM merge.
const BOM_NOT_FINAL_MESSAGES: Partial<Record<BuildStatus, string>> = {
	[BuildStatus.BomAwaitingReview]: 'Warning: BOM is still being edited by the customer. (State: Awaiting BOM Review)',
	[BuildStatus.ProcessingBom]: 'Warning: Customer-submitted BOM is still being merged. (State: Processing BOM)',
};

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/resolution-editor',
)({
	staticData: {
		authorize: (ability) => ability.can('manage', 'moduleResolution'),
		navigation: {
			label: 'Resolution Editor',
			keywords: ['resolutions', 'modules', 'packages'],
		},
	},
	validateSearch: z.object({
		modules: z.array(z.string()).optional().catch(undefined),
		tab: z.coerce.string().optional(),
		/** If "true", the module resolution table will temporarily ignore persisted state */
		ignoreFilters: z.enum(['true', 'false']).optional().catch(undefined),
	}),
	beforeLoad: ({ context: { trpc } }) => {
		return {
			getSigmaModelDefinitionsOptions: trpc.misc.getSigmaModelDefinitions.queryOptions({}, { staleTime: Infinity }),
		};
	},
	loader: async ({
		context: {
			queryClient,
			getProjectPreviewOptions,
			getProjectOptions,
			getRawProjectModelOptions,
			getModuleResolutionsOptions,
			getSigmaModelDefinitionsOptions,
		},
	}) => {
		void queryClient.ensureQueryData(getProjectOptions);
		void queryClient.ensureQueryData(getModuleResolutionsOptions);
		void queryClient.ensureQueryData(getRawProjectModelOptions);
		void queryClient.ensureQueryData(getSigmaModelDefinitionsOptions);
		const { projectPreview } = await queryClient.ensureQueryData(getProjectPreviewOptions);
		return { projectPreview };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: (loaderData ? loaderData.projectPreview.name : 'Project') + ' • Resolution Editor • Cadstrom',
			},
		],
	}),
	component: ResolutionEditorPage,
	pendingComponent: Loading,
});

function ResolutionEditorPage() {
	const navigate = Route.useNavigate();
	const {
		getProjectOptions,
		getRawProjectModelOptions,
		getModuleResolutionsOptions,
		getSigmaModelDefinitionsOptions,
		getRevisionOptions,
	} = Route.useRouteContext({
		select: ({
			getProjectOptions,
			getRawProjectModelOptions,
			getModuleResolutionsOptions,
			getSigmaModelDefinitionsOptions,
			getRevisionOptions,
		}) => ({
			getProjectOptions,
			getRawProjectModelOptions,
			getModuleResolutionsOptions,
			getSigmaModelDefinitionsOptions,
			getRevisionOptions,
		}),
	});
	const selectedModuleRefs = Route.useSearch({ select: (search) => search.modules ?? [] });
	const deferredSelectedModuleNames = useDeferredValue(selectedModuleRefs);

	const {
		data: { moduleResolutions },
	} = useSuspenseQuery(getModuleResolutionsOptions);
	const {
		data: { project },
	} = useSuspenseQuery(getProjectOptions);
	const {
		data: { projectModel },
	} = useSuspenseQuery(getRawProjectModelOptions);
	const {
		data: { sigmaModelDefinitions },
	} = useSuspenseQuery(getSigmaModelDefinitionsOptions);

	// The BOM warning is page furniture: a failure to read the revision must leave the editor itself
	// usable rather than trip the route error boundary.
	const { data: revisionData } = useQuery({ ...getRevisionOptions, throwOnError: false });
	const status = revisionData?.revision.status;
	const bomNotFinalMessage = status === undefined ? undefined : BOM_NOT_FINAL_MESSAGES[status];

	const cadRendererRef = useRef<CadRendererRef>(null);

	const selectedResolutions = useMemo(
		() =>
			deferredSelectedModuleNames.flatMap((name) => {
				const resolution = moduleResolutions.find((r) => r.moduleRef === name);
				if (resolution) {
					return [resolution];
				}
				// A module with no BOM item is still editable: writes against the stand-in are
				// addressed by module reference, which creates the record.
				return project.modules.some((mod) => mod.name === name) ? [createUnresolvedModuleResolution(name)] : [];
			}),
		[moduleResolutions, deferredSelectedModuleNames, project.modules],
	);
	const unifiedResolution = useMemo(() => getUnifiedModuleResolution(selectedResolutions), [selectedResolutions]);

	const selectedProjectModules = useMemo(
		() => project.modules.filter((mod) => deferredSelectedModuleNames.includes(mod.name)),
		[project.modules, deferredSelectedModuleNames],
	);

	/**
	 * Given a project module name (reference designator), finds the first instance of the module and commands the CAD
	 * renderer to zoom to it.
	 */
	const doZoomToModule = useCallback(
		(moduleRef: string | null, options: ZoomToModuleOptions) => {
			if (!cadRendererRef.current) {
				return;
			}

			if (moduleRef === null) {
				cadRendererRef.current.deselectAll();
			} else {
				const designModules = project.modules
					.find((mod) => mod.name === moduleRef)
					?.instances.map((instance) => {
						const design = projectModel.designs[instance.designId];
						if (design?.modelType !== 'schematic') {
							return undefined;
						}
						return design.modules.find((m) => m.ref === instance.ref);
					})
					.filter((m) => m !== undefined)
					.toSorted((a, b) => Utils.naturalStringSort(a.name, b.name));
				const firstDesignModule = designModules?.[0];

				if (firstDesignModule === undefined) {
					return;
				}

				cadRendererRef.current.zoomToModule(firstDesignModule.name, options);
			}
		},
		[projectModel.designs, project.modules],
	);

	// Called once the CAD renderer is initialized. Performs the initial zoom (after page load) to whatever module is
	// selected in the URL search params.
	const handleCadRendererRef: RefCallback<CadRendererRef | null> = useCallback(
		(ref) => {
			cadRendererRef.current = ref;
			if (!ref) {
				return;
			}

			// The module we'll zoom to is the last one in the list of selected modules (from the URL search params)
			const primarySelectedModule = selectedModuleRefs.at(-1);
			if (primarySelectedModule === undefined) {
				return;
			}

			doZoomToModule(primarySelectedModule, { onlyOnFirstTime: true });
		},
		[doZoomToModule, selectedModuleRefs],
	);

	useValidateResolutionEditorSingalMapping(project.modules, moduleResolutions, sigmaModelDefinitions);

	// Selected module state is stored in the URL search params
	const setSelectedModuleRefs = useCallback(
		async (moduleRefsOrCallback: string[] | ((prev: string[]) => string[])) => {
			await navigate({
				search: (prevSearch) => {
					const moduleRefs =
						typeof moduleRefsOrCallback === 'function' ?
							moduleRefsOrCallback(prevSearch.modules ?? [])
						:	moduleRefsOrCallback;

					// Zoom to the last selected module in the renderer
					const primarySelectedModule = moduleRefs.at(-1);
					if (primarySelectedModule !== undefined) {
						setTimeout(() => {
							doZoomToModule(primarySelectedModule, { onlyOnFirstTime: false });
						}, 2000);
					}

					return { ...prevSearch, modules: moduleRefs.length === 0 ? undefined : moduleRefs };
				},
				replace: true,
			});
		},
		[doZoomToModule, navigate],
	);

	// The "active selected module" is the one that is zoomed to in the CAD renderer.
	const setActiveSelectedModule = useCallback(
		(moduleRef: string) => {
			const selectedRefIndex = selectedModuleRefs.findIndex((ref) => ref === moduleRef);
			// Move the selected module to the end of the list
			const selectedModule = selectedModuleRefs[selectedRefIndex];
			if (selectedModule === undefined) {
				return;
			}
			const newList = selectedModuleRefs.toSpliced(selectedRefIndex, 1).concat(selectedModule);
			void setSelectedModuleRefs(newList);
		},
		[selectedModuleRefs, setSelectedModuleRefs],
	);

	const coreModel = useMemo(() => ({ coreModel: projectModel }), [projectModel]);
	return (
		<CoreModelContext value={coreModel}>
			<CadstromColorProviderContextProvider>
				<ActiveDesignContextProvider>
					<RegisterDesignSearchProvider />
					<RegisterNetSearchProvider />
					<RegisterModuleSearchProvider onModuleSelected={(m) => void setSelectedModuleRefs([m.name])} />
					<RegisterComponentInferenceProvider />

					<div id={ResolutionEditorPage.name} className='flex grow flex-col gap-2 px-2 pb-2'>
						{bomNotFinalMessage !== undefined && (
							<Alert severity='warning' variant='outlined'>
								{bomNotFinalMessage}
							</Alert>
						)}
						<ResizableLayout split='vertical' initialProportions={['1fr', 'min(40rem, 50%)']}>
							<div className='grid h-full min-h-0 w-full min-w-0 place-items-stretch'>
								<ModuleResolutionTable
									selectedModuleRefs={selectedModuleRefs}
									selectModuleRefs={setSelectedModuleRefs}
								/>
							</div>

							<ResizableLayout split='horizontal' initialProportions={['2fr', '1fr']} className='h-full'>
								<ResolutionDetails
									unifiedResolution={unifiedResolution}
									selectedResolutions={selectedResolutions}
									selectedProjectModules={selectedProjectModules}
									sigmaModelDefinitions={sigmaModelDefinitions}
									onModuleClick={setActiveSelectedModule}
								/>

								<div className='h-full overflow-hidden rounded-md border border-gray-a6'>
									<Suspense
										fallback={
											<div className='grid h-full w-full place-items-center'>
												<CircularProgress />
											</div>
										}
									>
										<ProjectRender ref={handleCadRendererRef} />
									</Suspense>
								</div>
							</ResizableLayout>
						</ResizableLayout>
					</div>
				</ActiveDesignContextProvider>
			</CadstromColorProviderContextProvider>
		</CoreModelContext>
	);
}
